import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import Layout from '@/components/Layout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { RefreshCw } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useMenu } from '@/contexts/MenuContext';
import { NutritionJournal } from '@/types';
import { Spinner } from '@/components/Spinner';
import { toast } from 'sonner';
import DaySelector from '@/components/nutrition-journal/DaySelector';
import NutritionTab from '@/components/nutrition-journal/NutritionTab';
import ActivityTab from '@/components/nutrition-journal/ActivityTab';
import NotesTab from '@/components/nutrition-journal/NotesTab';
import WeightTab from '@/components/nutrition-journal/WeightTab';
import VitalScoreTab from '@/components/nutrition-journal/VitalScoreTab';
import { syncNutritionGoalsWithProfile } from '@/utils/vitalScoreUtils';
import { supabase } from '@/integrations/supabase/client';
import { syncJournalWithMenu, getMenuDayFromDate } from '@/utils/menuJournalSync';

const DiarioNutricional = () => {
  const { user } = useAuth();
  const { weeklyMenu, getMenuTotals } = useMenu();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [journal, setJournal] = useState<NutritionJournal | null>(null);
  const [activeTab, setActiveTab] = useState('nutrition');
  const journalSyncRef = useRef(false);
  const menuSyncRef = useRef(false);
  const lastSavedJournalRef = useRef<string | null>(null);
  const weightUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastMenuSyncRef = useRef<number>(0);
  const menuSyncInProgressRef = useRef(false);
  const nutritionSummary = journal?.nutritionSummary;
  const profile = user?.profile;
  const initialLoadCompleteRef = useRef(false);
  const weightSyncInProgressRef = useRef<boolean>(false);
  const lastWeightSyncTimeRef = useRef<number>(0);
  const profileToJournalSyncCompleteRef = useRef<boolean>(false);

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1,
      y: 0,
      transition: { 
        duration: 0.6,
        ease: "easeOut",
        staggerChildren: 0.2 
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  const defaultIron = user?.profile?.gender === 'female' ? 18 : 8; // mg
  const defaultCalcium = 1000; // mg
  const defaultZinc = 11; // mg
  const defaultMagnesium = 400; // mg
  const defaultOmega3 = 1.6; // g
  
  const userWeight = user?.profile?.weight || 0;
  const calculatedProtein = userWeight > 0 ? Math.round(userWeight * 0.8) : 0;

  const handleRefreshJournal = async () => {
    if (!user || refreshing) return;
    
    setRefreshing(true);
    try {
      console.log('Manual refresh requested for journal');
      
      // Reset sync flags to allow fresh sync
      journalSyncRef.current = false;
      menuSyncRef.current = false;
      profileToJournalSyncCompleteRef.current = false;
      
      // Reload journal from database
      await getJournal();
      
      // Force menu sync if available
      if (weeklyMenu && journal) {
        await syncWithMenu();
      }
      
      toast.success('Diario actualizado correctamente');
    } catch (error) {
      console.error('Error refreshing journal:', error);
      toast.error('Error al actualizar el diario');
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (user) {
      getJournal();
    }
  }, [user, selectedDate]);

  useEffect(() => {
    if (user && journal && !journalSyncRef.current && !profileToJournalSyncCompleteRef.current) {
      profileToJournalSyncCompleteRef.current = true;
      const updatedJournal = syncNutritionGoalsWithProfile(journal, user);

      const hasRealChange = JSON.stringify(updatedJournal.nutritionSummary) !== JSON.stringify(journal.nutritionSummary)
        || Math.abs(updatedJournal.weight - journal.weight) > 0.01;

      if (hasRealChange) {
        console.log('🔁 Sync: journal nutrition/weight updated from profile');
        setJournal(updatedJournal);
        saveJournal(updatedJournal);
        journalSyncRef.current = true;
      }
    }
  }, [user, journal, user?.profile?.targetCalories, user?.profile?.targetProtein,
      user?.profile?.targetCarbs, user?.profile?.targetFat, user?.profile?.targetIron,
      user?.profile?.targetCalcium, user?.profile?.targetZinc, user?.profile?.targetMagnesium,
      user?.profile?.targetOmega3]);

  useEffect(() => {
    journalSyncRef.current = false;
    menuSyncRef.current = false;
    lastSavedJournalRef.current = null;
    profileToJournalSyncCompleteRef.current = false;
  }, [selectedDate, user?.id]);
  
  useEffect(() => {
    if (weeklyMenu && journal && user) {
      syncWithMenu();
    }
  }, [weeklyMenu, journal, selectedDate]);
  
  useEffect(() => {
    const handleMenuUpdated = () => {
      if (journal && user) {
        const now = Date.now();
        if (now - lastMenuSyncRef.current < 5000) {
          console.log('[JOURNAL] Throttling menu sync - too frequent');
          return;
        }
        
        console.log('[JOURNAL] Detectado cambio en menú, sincronizando...');
        menuSyncRef.current = false;
        syncWithMenu();
      }
    };
    
    window.addEventListener('menu_updated', handleMenuUpdated);
    window.addEventListener('storage', (event) => {
      if (event.key === 'menu_updated') {
        handleMenuUpdated();
      }
    });
    
    return () => {
      window.removeEventListener('menu_updated', handleMenuUpdated);
      window.removeEventListener('storage', (event) => {
        if (event.key === 'menu_updated') {
          handleMenuUpdated();
        }
      });
    };
  }, [journal, user?.id]);
  
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (!user || !journal) return;
      
      if (event.key === `journal_${journal.id}` && event.newValue) {
        try {
          const updatedJournal = JSON.parse(event.newValue);
          if (JSON.stringify(updatedJournal) !== JSON.stringify(journal)) {
            console.log('Journal updated from storage event:', updatedJournal.id);
            setJournal(updatedJournal);
          }
        } catch (error) {
          console.error('Error parsing journal from storage event:', error);
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [user, journal]);

  useEffect(() => {
    if (user && journal && user.profile?.weight > 0 && !weightSyncInProgressRef.current) {
      const currentWeight = parseFloat(String(journal.weight || 0));
      const profileWeight = parseFloat(String(user.profile.weight));

      const hasWeightChanged = Math.abs(currentWeight - profileWeight) > 0.1;
      const now = Date.now();
      const enoughTimePassed = now - lastWeightSyncTimeRef.current > 5000;

      if (hasWeightChanged && enoughTimePassed) {
        weightSyncInProgressRef.current = true;
        lastWeightSyncTimeRef.current = now;
        
        console.log(`🔁 Diario: Actualizando peso con el del perfil: ${currentWeight} → ${profileWeight}`);
        const updatedJournal = {
          ...journal,
          weight: profileWeight
        };
        setJournal(updatedJournal);
        saveJournal(updatedJournal);

        try {
          localStorage.setItem(`user_weight_${user.id}`, String(profileWeight));
          console.log('✅ Peso sincronizado y guardado en localStorage');
        } catch (err) {
          console.error('Error guardando peso en localStorage:', err);
        }
        
        setTimeout(() => {
          weightSyncInProgressRef.current = false;
        }, 5000);
      }
    }
  }, [user?.profile?.weight, journal]);

  useEffect(() => {
    const handler = (event: CustomEvent) => {
      console.log('[PROFILE] Profile updated event received');
      journalSyncRef.current = false;
      getJournal();
    };
    
    window.addEventListener('profile-updated', handler as EventListener);
    return () => window.removeEventListener('profile-updated', handler as EventListener);
  }, []);

  useEffect(() => {
    const handleJournalGoalsUpdated = (event: CustomEvent) => {
      if (!journal || !user?.id) return;
      
      console.log('[DIARIO] Detectada actualización de objetivos:', event.detail);
      
      if (event.detail && event.detail.userId === user.id && event.detail.goals) {
        try {
          const { goals } = event.detail;
          const updatedJournal = { ...journal };
          
          updatedJournal.nutritionSummary = {
            ...updatedJournal.nutritionSummary,
            goalCalories: Math.max(1000, Number(goals.calories)),
            goalProtein: Math.max(10, Number(goals.protein)),
            goalCarbs: Math.max(10, Number(goals.carbs)),
            goalFat: Math.max(10, Number(goals.fat)),
            goalIron: Math.max(1, Number(goals.iron)),
            goalCalcium: Math.max(100, Number(goals.calcium)),
            goalZinc: Math.max(1, Number(goals.zinc)),
            goalMagnesium: Math.max(50, Number(goals.magnesium)),
            goalOmega3: Math.max(0.1, Number(goals.omega3))
          };
          
          console.log('[DIARIO] Actualizando objetivos del diario:', updatedJournal.nutritionSummary);
          setJournal(updatedJournal);
          saveJournal(updatedJournal);
          toast.success('Objetivos nutricionales actualizados');
        } catch (error) {
          console.error('Error actualizando objetivos del diario:', error);
        }
      }
    };
    
    window.addEventListener('journal_goals_updated', handleJournalGoalsUpdated as EventListener);
    window.addEventListener('storage', (event) => {
      if (event.key === 'journal_updated' && event.newValue) {
        try {
          const data = JSON.parse(event.newValue);
          if (data.userId === user?.id && data.goals) {
            handleJournalGoalsUpdated(new CustomEvent('journal_goals_updated', { detail: data }));
          }
        } catch (error) {
          console.error('Error parsing journal updated event:', error);
        }
      }
    });
    
    return () => {
      window.removeEventListener('journal_goals_updated', handleJournalGoalsUpdated as EventListener);
      window.removeEventListener('storage', (event) => {
        if (event.key === 'journal_updated') {
          try {
            const data = JSON.parse(event.newValue || '{}');
            if (data.userId === user?.id && data.goals) {
              handleJournalGoalsUpdated(new CustomEvent('journal_goals_updated', { detail: data }));
            }
          } catch (error) {
            console.error('Error parsing journal updated event:', error);
          }
        }
      });
    };
  }, [journal, user?.id]);
  
  useEffect(() => {
    const handleNutritionUpdated = (event: CustomEvent) => {
      if (!journal || !user?.id) return;
      
      console.log('[DIARIO] Detectada actualización de nutrición:', event.detail);
      
      if (event.detail && event.detail.userId === user.id && event.detail.nutritionSummary) {
        try {
          const updatedJournal = { ...journal };
          
          updatedJournal.nutritionSummary = {
            ...updatedJournal.nutritionSummary,
            ...event.detail.nutritionSummary
          };
          
          console.log('[DIARIO] Actualizando nutrición del diario:', updatedJournal.nutritionSummary);
          setJournal(updatedJournal);
          saveJournal(updatedJournal);
          
          const syncEvent = new CustomEvent('nutrition-synced', {
            detail: { 
              nutritionSummary: updatedJournal.nutritionSummary,
              userId: user.id
            }
          });
          window.dispatchEvent(syncEvent);
        } catch (error) {
          console.error('Error actualizando nutrición del diario:', error);
        }
      }
    };
    
    window.addEventListener('nutrition-updated', handleNutritionUpdated as EventListener);
    return () => window.removeEventListener('nutrition-updated', handleNutritionUpdated as EventListener);
  }, [journal, user?.id]);
  
  const syncWithMenu = async () => {
    if (!weeklyMenu || !journal || !user || menuSyncRef.current || menuSyncInProgressRef.current) return;
    
    try {
      menuSyncRef.current = true;
      menuSyncInProgressRef.current = true;
      lastMenuSyncRef.current = Date.now();
      
      const menuDay = getMenuDayFromDate(selectedDate);
      const menuTotals = getMenuTotals(menuDay);
      
      if (menuTotals) {
        console.log('[JOURNAL] Sincronizando con menú del día:', menuDay);
        console.log('[JOURNAL] Totales del menú:', menuTotals);
        
        const updatedJournal = await syncJournalWithMenu(journal, menuDay, menuTotals, user.id);
        
        if (JSON.stringify(updatedJournal.nutritionSummary) !== JSON.stringify(journal.nutritionSummary)) {
          setJournal(updatedJournal);
        }
      }
      
      setTimeout(() => {
        menuSyncRef.current = false;
        menuSyncInProgressRef.current = false;
      }, 1000);
    } catch (error) {
      console.error('Error sincronizando con menú:', error);
      menuSyncRef.current = false;
      menuSyncInProgressRef.current = false;
    }
  };

  const getJournal = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const dateString = format(selectedDate, 'yyyy-MM-dd');
      const journalId = `${user.id}_${dateString}`;

      console.log('Getting journal:', journalId);

      const supabaseJournal = await fetchFromSupabase(journalId);
      if (supabaseJournal) {
        setLoading(false);
        return;
      }

      const storedJournal = localStorage.getItem(`journal_${journalId}`);

      if (storedJournal) {
        const parsedJournal = JSON.parse(storedJournal);
        console.log('Retrieved journal from localStorage:', parsedJournal);
        setJournal(parsedJournal);
        setLoading(false);

        const updatedJournal = syncNutritionGoalsWithProfile(parsedJournal, user);
        if (JSON.stringify(updatedJournal.nutritionSummary) !== JSON.stringify(parsedJournal.nutritionSummary)) {
          console.log('Syncing stored journal with current user profile:', updatedJournal.nutritionSummary);
          setJournal(updatedJournal);
          saveJournal(updatedJournal);
        }

        return;
      }

      createNewJournal(journalId, dateString);
    } catch (error) {
      console.error('Error getting journal:', error);
      toast.error('Error al cargar el diario nutricional');
      setLoading(false);
    }
  };

  const createNewJournal = (journalId: string, dateString: string) => {
    if (!user) return;

    console.log("Creating new journal for date:", dateString);

    const userWeight = user?.profile?.weight && user.profile.weight > 0
      ? user.profile.weight
      : 70;

    const newJournal: NutritionJournal = {
      id: journalId,
      userId: user.id,
      date: dateString,
      nutritionSummary: {
        caloriesConsumed: 0,
        goalCalories: user.profile?.targetCalories || 0,
        proteinConsumed: 0,
        goalProtein: user.profile?.targetProtein || (userWeight > 0 ? Math.round(userWeight * 0.8) : 0),
        carbsConsumed: 0,
        goalCarbs: user.profile?.targetCarbs || 0,
        fatConsumed: 0,
        goalFat: user.profile?.targetFat || 0,
        ironConsumed: 0,
        goalIron: user.profile?.targetIron || 0,
        calciumConsumed: 0,
        goalCalcium: user.profile?.targetCalcium || 0,
        zincConsumed: 0,
        goalZinc: user.profile?.targetZinc || 0,
        magnesiumConsumed: 0,
        goalMagnesium: user.profile?.targetMagnesium || 0,
        vitaminCConsumed: 0,
        goalVitaminC: 0,
        omega3Consumed: 0,
        goalOmega3: user.profile?.targetOmega3 || 0
      },
      meals: [],
      steps: 0,
      workout: {
        type: 'cardio',
        minutes: 0,
        intensity: 'medium'
      },
      weight: userWeight,
      bodyFat: 0,
      bodyWater: 0,
      muscleMass: 0,
      restLevel: 5,
      energyLevel: 5,
      stressLevel: 3,
      waterIntake: 0,
      sunExposure: 0,
      notes: '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    setJournal(newJournal);
    setLoading(false);
    saveJournal(newJournal, true);

    const hasNutritionData = newJournal.nutritionSummary.goalCalories > 0 || 
                             newJournal.nutritionSummary.goalProtein > 0;
                             
    if (hasNutritionData) {
      const event = new CustomEvent('nutrition-synced', {
        detail: {
          nutritionSummary: newJournal.nutritionSummary,
          userId: user.id
        }
      });
      window.dispatchEvent(event);
    }
  };
  
  const fetchFromSupabase = async (journalId: string) => {
    try {
      const { data: journalData, error } = await supabase
        .from('nutrition_journals')
        .select('*')
        .eq('id', journalId)
        .single();
      
      if (!error && journalData) {
        console.log('Journal loaded from Supabase:', journalData);
        console.log('Water intake from Supabase:', journalData.water_intake);
        
        const parsedJournal: NutritionJournal = {
          ...journalData,
          waterIntake: journalData.water_intake || 0,
          sunExposure: journalData.sun_exposure || 0,
          restLevel: journalData.rest_level || 5,
          energyLevel: journalData.energy_level || 5,
          steps: journalData.steps || 0,
          workout: journalData.workout || {
            type: 'cardio',
            minutes: 0,
            intensity: 'medium',
            description: ''
          }
        } as NutritionJournal;
        
        const updatedJournal = syncNutritionGoalsWithProfile(parsedJournal, user);
        
        console.log('Water intake after sync:', updatedJournal.waterIntake);
        setJournal(updatedJournal);
        localStorage.setItem(`journal_${journalId}`, JSON.stringify(updatedJournal));
        
        if (JSON.stringify(updatedJournal.nutritionSummary) !== JSON.stringify(parsedJournal.nutritionSummary)) {
          console.log('Syncing Supabase journal with current user profile:', updatedJournal.nutritionSummary);
          saveJournal(updatedJournal);
        }
        
        return updatedJournal;
      }
      
      return null;
    } catch (err) {
      console.error('Error fetching journal from Supabase:', err);
      return null;
    }
  };
  
  const saveJournal = async (updatedJournal: NutritionJournal, isNew: boolean = false) => {
    if (!user) return;
    
    try {
      const journalId = updatedJournal.id;
      
      const journalString = JSON.stringify(updatedJournal);
      if (journalString === lastSavedJournalRef.current) {
        console.log('Skipping journal save, no changes detected');
        return;
      }
      
      console.log('Saving journal:', journalId);
      console.log('Activity data:', {
        waterIntake: updatedJournal.waterIntake,
        steps: updatedJournal.steps,
        sunExposure: updatedJournal.sunExposure,
        restLevel: updatedJournal.restLevel,
        energyLevel: updatedJournal.energyLevel,
        workout: updatedJournal.workout
      });
      
      updatedJournal.updatedAt = new Date().toISOString();
      
      if (!updatedJournal.workout) {
        updatedJournal.workout = {
          type: 'cardio',
          minutes: 0,
          intensity: 'medium',
          description: ''
        };
      } else if (!updatedJournal.workout.intensity) {
        updatedJournal.workout.intensity = 'medium';
      }
      
      localStorage.setItem(`journal_${journalId}`, journalString);
      lastSavedJournalRef.current = journalString;
      
      try {
        const storageEvent = new StorageEvent('storage', {
          key: `journal_${journalId}`,
          newValue: journalString,
          url: window.location.href
        });
        window.dispatchEvent(storageEvent);
      } catch (error) {
        console.error('Error dispatching manual storage event:', error);
      }
      
      if (!user.id.startsWith('user-')) {
        await saveJournalToSupabase(updatedJournal);
      }
      
      if (!isNew) {
        toast.success('Diario actualizado correctamente');
      }
    } catch (error) {
      console.error('Error saving journal:', error);
      toast.error('Error al guardar el diario nutricional');
    }
  };
  
  const saveJournalToSupabase = async (journal: NutritionJournal) => {
    if (!user || user.id.startsWith('user-')) return;
    
    try {
      console.log('Saving to Supabase - Activity data:', {
        waterIntake: journal.waterIntake,
        steps: journal.steps,
        sunExposure: journal.sunExposure,
        restLevel: journal.restLevel,
        energyLevel: journal.energyLevel,
        workout: journal.workout
      });
      
      const userId = journal.userId || user.id;
      
      const { data: existingJournal, error: checkError } = await supabase
        .from('nutrition_journals')
        .select('id')
        .eq('id', journal.id)
        .single();
        
      let operation;
      
      if (checkError && checkError.code === 'PGRST116') {
        operation = supabase
          .from('nutrition_journals')
          .insert({
            id: journal.id,
            user_id: userId,
            date: journal.date,
            nutrition_summary: journal.nutritionSummary,
            meals: journal.meals,
            steps: journal.steps,
            workout: journal.workout,
            weight: journal.weight,
            body_fat: journal.bodyFat,
            body_water: journal.bodyWater,
            muscle_mass: journal.muscleMass,
            rest_level: journal.restLevel,
            energy_level: journal.energyLevel,
            stress_level: journal.stressLevel,
            water_intake: journal.waterIntake,
            sun_exposure: journal.sunExposure,
            notes: journal.notes,
            created_at: journal.createdAt,
            updated_at: journal.updatedAt
          });
      } else {
        operation = supabase
          .from('nutrition_journals')
          .update({
            nutrition_summary: journal.nutritionSummary,
            meals: journal.meals,
            steps: journal.steps,
            workout: journal.workout,
            weight: journal.weight,
            body_fat: journal.bodyFat,
            body_water: journal.bodyWater,
            muscle_mass: journal.muscleMass,
            rest_level: journal.restLevel,
            energy_level: journal.energyLevel,
            stress_level: journal.stressLevel,
            water_intake: journal.waterIntake,
            sun_exposure: journal.sunExposure,
            notes: journal.notes,
            updated_at: journal.updatedAt
          })
          .eq('id', journal.id)
          .eq('user_id', userId);
      }
      
      const { data, error } = await operation;
      
      if (error) {
        console.error('Error saving journal to Supabase:', error);
      } else {
        console.log('Journal saved to Supabase successfully:', data);
      }
    } catch (error) {
      console.error('Error saving journal to Supabase:', error);
    }
  };

  const setJournalWithSave = (updatedJournal: NutritionJournal) => {
    if (!updatedJournal || !journal) return;

    const journalCopy = JSON.parse(JSON.stringify(updatedJournal));
    journalCopy.updatedAt = new Date().toISOString();

    const nutritionSummaryChanged = JSON.stringify(journal.nutritionSummary) !== JSON.stringify(journalCopy.nutritionSummary);
    const weightChanged = Math.abs(journal.weight - journalCopy.weight) > 0.01;

    setJournal(journalCopy);
    saveJournal(journalCopy);

    if (user && nutritionSummaryChanged && !weightChanged) {
      const event = new CustomEvent('nutrition-synced', {
        detail: { 
          nutritionSummary: journalCopy.nutritionSummary,
          userId: user.id
        }
      });
      window.dispatchEvent(event);
    }
  };

  useEffect(() => {
    if (journal && journal.meals) {
      console.log('[DiarioNutricional] Journal meals updated, rerendering:', 
        journal.meals.length, 
        'Total calories:', journal.nutritionSummary?.caloriesConsumed);
    }
  }, [journal?.meals, journal?.nutritionSummary?.caloriesConsumed]);
  
  useEffect(() => {
    if (!user?.id || !journal) return;
    
    const handleWeightUpdated = (event: CustomEvent) => {
      if (event.detail && event.detail.userId === user.id && event.detail.weight) {
        console.log('Weight update event received in DiarioNutricional:', event.detail.weight);
        
        const newWeight = parseFloat(String(event.detail.weight));
        const currentWeight = parseFloat(String(journal.weight));
        
        if (!isNaN(newWeight) && Math.abs(newWeight - currentWeight) > 0.1) {
          console.log(`Updating journal weight: ${currentWeight} -> ${newWeight}`);
          
          const updatedJournal = { 
            ...journal,
            weight: newWeight
          };
          
          setJournal(updatedJournal);
          saveJournal(updatedJournal);
          
          try {
            localStorage.setItem(`user_weight_${user.id}`, String(newWeight));
            console.log('DiarioNutricional - Updated localStorage weight on weight_updated event:', newWeight);
          } catch (error) {
            console.error('Error storing updated weight in localStorage:', error);
          }
        }
      }
    };
    
    window.addEventListener('weight_updated', handleWeightUpdated as EventListener);
    
    return () => {
      window.removeEventListener('weight_updated', handleWeightUpdated as EventListener);
    };
  }, [journal, user?.id, setJournal]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!journal) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">No se pudo cargar el diario nutricional.</p>
      </div>
    );
  }

  const targetCalories = journal?.nutritionSummary?.goalCalories || user?.profile?.targetCalories || 0;
  const targetProtein = journal?.nutritionSummary?.goalProtein || calculatedProtein;
  const targetCarbs = journal?.nutritionSummary?.goalCarbs || user?.profile?.targetCarbs || 0;
  const targetFat = journal?.nutritionSummary?.goalFat || user?.profile?.targetFat || 0;
  const targetIron = journal?.nutritionSummary?.goalIron || user?.profile?.targetIron || 0;
  const targetCalcium = journal?.nutritionSummary?.goalCalcium || user?.profile?.targetCalcium || 0;
  const targetZinc = journal?.nutritionSummary?.goalZinc || user?.profile?.targetZinc || 0;
  const targetMagnesium = journal?.nutritionSummary?.goalMagnesium || user?.profile?.targetMagnesium || 0;
  const targetOmega3 = journal?.nutritionSummary?.goalOmega3 || user?.profile?.targetOmega3 || 0;

  const caloriesProgress = (journal?.nutritionSummary?.caloriesConsumed && targetCalories > 0) ? 
    Math.min(Math.round((journal.nutritionSummary.caloriesConsumed / targetCalories) * 100), 100) : 0;
  const proteinProgress = (journal?.nutritionSummary?.proteinConsumed && targetProtein > 0) ? 
    Math.min(Math.round((journal.nutritionSummary.proteinConsumed / targetProtein * 100)), 100) : 0;
  const carbsProgress = (journal?.nutritionSummary?.carbsConsumed && targetCarbs > 0) ? 
    Math.min(Math.round((journal.nutritionSummary.carbsConsumed / targetCarbs * 100)), 100) : 0;
  const fatProgress = (journal?.nutritionSummary?.fatConsumed && targetFat > 0) ? 
    Math.min(Math.round((journal.nutritionSummary.fatConsumed / targetFat * 100)), 100) : 0;
  const ironProgress = (journal?.nutritionSummary?.ironConsumed && targetIron > 0) ? 
    Math.min(Math.round((journal.nutritionSummary.ironConsumed / targetIron * 100)), 100) : 0;
  const calciumProgress = (journal?.nutritionSummary?.calciumConsumed && targetCalcium > 0) ? 
    Math.min(Math.round((journal.nutritionSummary.calciumConsumed / targetCalcium * 100)), 100) : 0;
  const zincProgress = (journal?.nutritionSummary?.zincConsumed && targetZinc > 0) ? 
    Math.min(Math.round((journal.nutritionSummary.zincConsumed / targetZinc * 100)), 100) : 0;
  const magnesiumProgress = (journal?.nutritionSummary?.magnesiumConsumed && targetMagnesium > 0) ? 
    Math.min(Math.round((journal.nutritionSummary.magnesiumConsumed / targetMagnesium * 100)), 100) : 0;
  const omega3Progress = (journal?.nutritionSummary?.omega3Consumed && targetOmega3 > 0) ? 
    Math.min(Math.round((journal.nutritionSummary.omega3Consumed / targetOmega3 * 100)), 100) : 0;

  const pageInfo = {
    title: 'Diario de Hábitos',
    description: 'Registra tus comidas, actividades y notas diarias para llevar un control de tu salud.'
  };

  return (
    <Layout pageInfo={pageInfo}>
      <div className="w-full p-2 bg-gradient-to-br from-slate-50 to-white min-h-screen">
        <div className="w-full max-w-none space-y-6 relative">
          <div className="bg-gradient-to-r from-purple-600/5 to-blue-500/5 rounded-2xl p-6 backdrop-blur-sm border border-purple-100 pb-4">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-purple-600 to-blue-500 bg-clip-text text-transparent">
                Diario de Hábitos
              </h1>
              
              <Button
                onClick={handleRefreshJournal}
                disabled={refreshing}
                variant="outline"
                size="sm"
                className="flex items-center gap-2 border-purple-200 text-purple-700 hover:bg-purple-50"
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Actualizando...' : 'Actualizar Diario'}
              </Button>
            </div>
            
            <DaySelector 
              selectedDate={selectedDate} 
              setSelectedDate={setSelectedDate}
            />
          </div>
          
          <div className="mt-6">
            <VitalScoreTab 
              journal={journal}
              user={user}
              containerVariants={containerVariants}
              itemVariants={itemVariants}
            />
          </div>

          <div className="bg-white rounded-2xl shadow-lg shadow-purple-100/50 backdrop-blur-lg border border-purple-50">
            <Tabs defaultValue="nutrition" className="p-4" value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 p-1 rounded-xl">
                <TabsTrigger value="nutrition" className="data-[state=active]:bg-white data-[state=active]:text-purple-700">
                  Nutrición
                </TabsTrigger>
                <TabsTrigger value="activity" className="data-[state=active]:bg-white data-[state=active]:text-purple-700">
                  Actividad
                </TabsTrigger>
                <TabsTrigger value="notes" className="data-[state=active]:bg-white data-[state=active]:text-purple-700">
                  Notas
                </TabsTrigger>
                <TabsTrigger value="weight" className="data-[state=active]:bg-white data-[state=active]:text-purple-700">
                  Peso
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="nutrition" className="mt-6">
                <NutritionTab 
                  journal={journal} 
                  setJournal={setJournalWithSave} 
                  containerVariants={containerVariants}
                  itemVariants={itemVariants}
                />
              </TabsContent>
              
              <TabsContent value="activity" className="mt-6">
                <ActivityTab 
                  journal={journal} 
                  setJournal={setJournalWithSave} 
                  containerVariants={containerVariants}
                  itemVariants={itemVariants}
                />
              </TabsContent>
              
              <TabsContent value="notes" className="mt-6">
                <NotesTab 
                  journal={journal} 
                  setJournal={setJournalWithSave} 
                  containerVariants={containerVariants}
                  itemVariants={itemVariants}
                />
              </TabsContent>
              
              <TabsContent value="weight" className="mt-6">
                <WeightTab 
                  journal={journal} 
                  setJournal={setJournalWithSave} 
                  containerVariants={containerVariants}
                  itemVariants={itemVariants}
                />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DiarioNutricional;

/**
 * NOTA:
 * Después de guardar el perfil en la pantalla de Perfil Nutricional
 * (function handleSave en src/pages/Nutrition.tsx o src/pages/Profile.tsx),
 * es muy recomendable llamar:
 *   await loadProfile();
 * de modo que la última versión se traiga directamente desde Supabase
 * para evitar bucles y desincronización temporal.
 */
